import { CtaToast } from '@shared/types'
import { defineNuxtPlugin } from '#imports'
import { StatusCodes } from 'http-status-codes'
import {
  ErrorType,
  isThrownException,
  GRPC_REQUEST_FAILED,
  TransactionException,
  GrpcUnaryRequestException
} from '@injectivelabs/exceptions'
import { normalizeUnknownError } from '@/app/utils/error'
import { mapContractErrorToI18nKey } from '@/app/utils/errorMap'
import { captureLazySentryException } from '@/app/providers/sentry/lazy'
import {
  PENDING_TRANSACTION_MESSAGE,
  GEO_RESTRICTED_ACTION_MESSAGE
} from '@/app/data/errors'
import type { ThrownException } from '@injectivelabs/exceptions'

const MAX_BUSY_ACTIONS_SENT_TO_SENTRY = 5

const reportToUser = (error: ThrownException) => {
  const nuxtApp = useNuxtApp()
  const notificationStore = useSharedNotificationStore()
  const { isTurnkeyWalletSessionException } = useTurnkeySession()
  const { t } = nuxtApp?.$i18n || {}

  const isFailedToFetch = (error.message || '')
    .toLowerCase()
    .startsWith('failed to fetch')

  // Timedout requests happening in the background should not be reported to the user
  if (
    isFailedToFetch ||
    (error.type === ErrorType.HttpRequest &&
      error.code === StatusCodes.REQUEST_TOO_LONG) ||
    normalizeUnknownError(error).message === GEO_RESTRICTED_ACTION_MESSAGE
  ) {
    return
  }

  if (
    error instanceof GrpcUnaryRequestException &&
    error.contextCode === GRPC_REQUEST_FAILED
  ) {
    return notificationStore.error({
      title: 'High demand detected',
      description: 'Engineers are improving performance and efficiency.'
    })
  }

  if (isTurnkeyWalletSessionException(error) && t) {
    return notificationStore.error({
      title: t('toast.wallet.sessionExpired')
    })
  }

  const friendlyKey = mapContractErrorToI18nKey(error)
  if (friendlyKey && t) {
    return notificationStore.error({ title: t(friendlyKey) })
  }

  if (!(error instanceof TransactionException)) {
    return notificationStore.error({
      title: error.message || 'Something happened'
    })
  }

  notificationStore.error({
    title: error.message,
    context: error.originalMessage
  })
}

const reportTurnkeyDisconnectErrorToSentry = (error: unknown) => {
  captureLazySentryException(error, {
    message: 'Failed to disconnect expired Turnkey wallet session',
    attributes: {
      feature: 'wallet',
      operation: 'turnkey-session-disconnect'
    }
  })
}

const handleTurnkeyWalletSessionError = (error: unknown) => {
  const {
    isTurnkeyWalletSessionException,
    handleTurnkeyWalletSessionException
  } = useTurnkeySession()

  if (!isTurnkeyWalletSessionException(error)) {
    return false
  }

  handleTurnkeyWalletSessionException({
    onDisconnectError: reportTurnkeyDisconnectErrorToSentry
  })

  return true
}

const reportToSentry = (error: ThrownException) => {
  const compactError = error.toCompactError()
  const isLegacyIgnoredError = (
    [ErrorType.Unspecified, ErrorType.WalletError] as ErrorType[]
  ).includes(error.type)
  const additionalFields = appendAdditionalFieldsBasedOnError(error)

  captureLazySentryException(error, {
    message: compactError.message,
    attributes: {
      feature: 'errors',
      operation: 'handled-exception',
      errorCode: error.code,
      errorType: error.type,
      errorName: error.name,
      compactMessage: compactError.message,
      isLegacyIgnoredError,
      ...additionalFields
    }
  })
}

function appendAdditionalFieldsBasedOnError(error: ThrownException) {
  const normalizedError = normalizeUnknownError(error)

  if (normalizedError.message.startsWith(PENDING_TRANSACTION_MESSAGE)) {
    const appStore = useAppStore()
    const busyAction = appStore.busyActions[appStore.busyActions.length - 1]
    const busyActions = appStore.busyActions.slice(
      -MAX_BUSY_ACTIONS_SENT_TO_SENTRY
    )

    if (!busyAction) {
      return {}
    }

    return {
      walletQueueBusyAction: busyAction,
      walletQueueBusyActions: busyActions.join(',')
    }
  }

  return {}
}

const reportUnknownErrorToSentry = (error: unknown) => {
  const normalizedError = normalizeUnknownError(error)

  captureLazySentryException(error, {
    message: `${normalizedError.message} is not handled as an Exception`,
    attributes: {
      feature: 'errors',
      operation: 'unknown-exception',
      ...getErrorAttributes(normalizedError)
    }
  })
}

function getErrorAttributes(error: Error) {
  return {
    ...getErrorCustomAttributes(error),
    errorName: error.name,
    errorMessage: error.message,
    errorCause: getErrorCause(error)
  }
}

function getErrorCause(error: Error) {
  const cause = error.cause

  if (cause instanceof Error) {
    return {
      name: cause.name,
      message: cause.message
    }
  }

  if (['string', 'number', 'boolean'].includes(typeof cause)) {
    return cause
  }
}

function getErrorCustomAttributes(error: Error) {
  return Object.fromEntries(
    Object.entries(error).filter(([key, value]) => {
      return (
        key !== 'cause' &&
        ['string', 'number', 'boolean'].includes(typeof value)
      )
    })
  )
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.hook('app:error', (error) => {
    captureLazySentryException(error, {
      attributes: {
        feature: 'errors',
        operation: 'vue-error-fatal'
      }
    })
  })

  nuxtApp.hook('app:suspense:resolve', () => {
    nuxtApp.vueApp.config.errorHandler = (error, context, info) => {
      captureLazySentryException(error, {
        attributes: {
          feature: 'errors',
          operation: 'vue-error',
          vueInfo: info,
          componentName: context?.$options?.name
        }
      })
    }
  })

  window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    const error = event.reason
    const notificationStore = useSharedNotificationStore()

    if (notificationStore.isErrorNotificationHandled(error)) {
      return
    }

    const isTurnkeyWalletSessionError = handleTurnkeyWalletSessionError(error)

    if (!isThrownException(error)) {
      reportUnknownErrorToSentry(error)
    } else {
      if (isTurnkeyWalletSessionError) {
        reportToUser(error as ThrownException)
      }

      reportToSentry(error)
    }
  }

  const errorHandler = (error: unknown) => {
    const notificationStore = useSharedNotificationStore()

    handleTurnkeyWalletSessionError(error)

    notificationStore.close(CtaToast.Telemetry)

    if (notificationStore.isErrorNotificationHandled(error)) {
      return
    }

    if (!(error instanceof Error) || !isThrownException(error)) {
      return reportUnknownErrorToSentry(error)
    }

    const thrownError = error as ThrownException

    reportToUser(thrownError)
    reportToSentry(thrownError)
  }

  return {
    provide: {
      onError: errorHandler
    }
  }
})
